import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Building2, FileText, TrendingUp, ShieldCheck, ShieldAlert, LayoutDashboard, LogOut, Home } from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { load(); }, [activeTab]);

  const load = async () => {
    try {
      const s = await api.get('/admin/stats');
      setStats(s.data);
      if (activeTab === 'brokers' || activeTab === 'overview') {
        const b = await api.get('/admin/brokers');
        setBrokers(b.data);
      }
      if (activeTab === 'projects') {
        const p = await api.get('/admin/projects');
        setProjects(p.data);
      }
      if (activeTab === 'leads') {
        const l = await api.get('/admin/leads');
        setLeads(l.data);
      }
    } catch (e) { console.error(e); }
  };

  const approveBroker = async (id) => {
    try {
      await api.put(`/admin/brokers/${id}/approve`);
      toast.success('Broker approved');
      setSelectedBroker(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const rejectBroker = async (id) => {
    if (!rejectReason) { toast.error('Provide a reason'); return; }
    try {
      await api.put(`/admin/brokers/${id}/reject`, { reason: rejectReason });
      toast.success('Broker rejected');
      setSelectedBroker(null);
      setRejectReason('');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const approveProject = async (id) => {
    try {
      await api.put(`/admin/projects/${id}/approve`);
      toast.success('Project published');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const rejectProject = async (id) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    try {
      await api.put(`/admin/projects/${id}/reject`, { reason });
      toast.success('Project rejected');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black">P</div>
            <div>
              <div className="font-display font-extrabold text-[#0F2C59]">PropertyByKilo</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Admin Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold hidden sm:block">{user?.name}</span>
            <button onClick={() => navigate('/')} className="p-2 text-slate-500 hover:text-[#0F2C59]" title="Home" data-testid="admin-home-btn"><Home size={18}/></button>
            <button onClick={handleLogout} className="p-2 text-slate-500 hover:text-red-600" title="Logout" data-testid="admin-logout-btn"><LogOut size={18}/></button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0F2C59]" data-testid="admin-title">Admin Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Monitor and manage the entire PropertyByKilo ecosystem</p>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Brokers', value: stats.total_brokers, icon: Users, color: 'bg-blue-50 text-blue-600' },
              { label: 'Pending Approval', value: stats.pending_brokers, icon: ShieldAlert, color: 'bg-amber-50 text-amber-600' },
              { label: 'Approved Brokers', value: stats.approved_brokers, icon: ShieldCheck, color: 'bg-emerald-50 text-emerald-600' },
              { label: 'Total Projects', value: stats.total_projects, icon: Building2, color: 'bg-purple-50 text-purple-600' },
              { label: 'Published', value: stats.published_projects, icon: Building2, color: 'bg-emerald-50 text-emerald-600' },
              { label: 'Landing Pages', value: stats.total_landing_pages, icon: FileText, color: 'bg-slate-100 text-[#0F2C59]' },
              { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'bg-orange-50 text-[#F97316]' },
              { label: 'This Month', value: stats.monthly_leads, icon: TrendingUp, color: 'bg-red-50 text-red-600' }
            ].map(s => (
              <div key={s.label} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`w-9 h-9 rounded-md ${s.color} flex items-center justify-center`}>
                  <s.icon size={16} />
                </div>
                <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</div>
                <div className="font-display font-extrabold text-2xl text-[#0F2C59] mt-1">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList>
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="brokers" data-testid="admin-tab-brokers">Brokers ({stats?.pending_brokers || 0} pending)</TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-6">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-display font-bold text-lg text-[#0F2C59] mb-3">Recent Broker Applications</h3>
              {brokers.filter(b => b.status === 'submitted').length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No pending applications</div>
              ) : (
                <div className="space-y-2">
                  {brokers.filter(b => b.status === 'submitted').slice(0, 5).map(b => (
                    <div key={b.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-md">
                      <div>
                        <div className="font-semibold">{b.full_name}</div>
                        <div className="text-xs text-slate-500">{b.city} • RERA: {b.rera_number}</div>
                      </div>
                      <Button size="sm" onClick={() => setSelectedBroker(b)} data-testid={`review-broker-${b.id}`}>Review</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Brokers */}
          <TabsContent value="brokers" className="mt-6">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Name</th>
                      <th className="px-4 py-3 text-left font-semibold">City</th>
                      <th className="px-4 py-3 text-left font-semibold">RERA</th>
                      <th className="px-4 py-3 text-left font-semibold">Type</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {brokers.map(b => (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid={`broker-row-${b.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{b.full_name || '—'}</div>
                          {b.agency_name && <div className="text-xs text-slate-500">{b.agency_name}</div>}
                        </td>
                        <td className="px-4 py-3">{b.primary_city || b.city || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{b.rera_number || '—'}</td>
                        <td className="px-4 py-3 capitalize">{b.broker_type}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${b.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : b.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' : b.status === 'submitted' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-700'} border`}>
                            {b.status?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button size="sm" variant="outline" onClick={() => setSelectedBroker(b)} data-testid={`view-broker-${b.id}`}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Projects */}
          <TabsContent value="projects" className="mt-6">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Project</th>
                      <th className="px-4 py-3 text-left font-semibold">Location</th>
                      <th className="px-4 py-3 text-left font-semibold">Price</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {projects.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-project-${p.id}`}>
                        <td className="px-4 py-3 font-semibold">{p.name}</td>
                        <td className="px-4 py-3">{p.locality}, {p.city}</td>
                        <td className="px-4 py-3 font-mono">₹{p.price_min?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${p.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : p.status === 'submitted' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-700'} border`}>
                            {p.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {p.status === 'submitted' && (
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => approveProject(p.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-project-${p.id}`}>Approve</Button>
                              <Button size="sm" variant="outline" onClick={() => rejectProject(p.id)} data-testid={`reject-project-${p.id}`}>Reject</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Leads */}
          <TabsContent value="leads" className="mt-6">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Buyer</th>
                      <th className="px-4 py-3 text-left font-semibold">Mobile</th>
                      <th className="px-4 py-3 text-left font-semibold">Source</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {leads.map(l => (
                      <tr key={l.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold">{l.name}</td>
                        <td className="px-4 py-3 font-mono text-xs">{l.mobile}</td>
                        <td className="px-4 py-3 capitalize">{l.source?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3"><Badge className="text-xs bg-slate-100 border border-slate-200 text-slate-700">{l.status?.replace(/_/g, ' ')}</Badge></td>
                        <td className="px-4 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Broker Review Dialog */}
      <Dialog open={!!selectedBroker} onOpenChange={(o) => !o && setSelectedBroker(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBroker && (
            <>
              <DialogHeader>
                <DialogTitle>Broker Application Review</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">Full Name</div><div>{selectedBroker.full_name}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">City</div><div>{selectedBroker.primary_city || selectedBroker.city}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">Type</div><div className="capitalize">{selectedBroker.broker_type}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">Agency</div><div>{selectedBroker.agency_name || '—'}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">Experience</div><div>{selectedBroker.years_of_experience} years</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">RERA</div><div className="font-mono">{selectedBroker.rera_number}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">WhatsApp</div><div>{selectedBroker.whatsapp_number || '—'}</div></div>
                  <div><div className="text-xs uppercase text-slate-500 font-semibold">GST</div><div>{selectedBroker.gst_number || '—'}</div></div>
                </div>

                {selectedBroker.bio && (
                  <div>
                    <div className="text-xs uppercase text-slate-500 font-semibold mb-1">Bio</div>
                    <div className="p-3 bg-slate-50 rounded-md text-sm">{selectedBroker.bio}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Documents</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {['pan_document_url', 'aadhaar_document_url', 'rera_certificate_url', 'address_proof_url'].map(f => (
                      <div key={f} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${selectedBroker[f] ? 'bg-emerald-500' : 'bg-red-500'}`}/>
                        <span>{f.replace(/_url/, '').replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedBroker.status === 'submitted' && (
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex gap-3">
                      <Button onClick={() => approveBroker(selectedBroker.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-broker-${selectedBroker.id}`}>
                        <ShieldCheck size={16} className="mr-2"/> Approve Broker
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Textarea placeholder="Rejection reason (required to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} data-testid="reject-reason-input" />
                      <Button onClick={() => rejectBroker(selectedBroker.id)} variant="outline" className="w-full border-red-300 text-red-700 hover:bg-red-50" data-testid={`reject-broker-${selectedBroker.id}`}>
                        Reject Application
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
